// hook กลางสำหรับเรียก AI + คิด token (ใช้ทั้งหน้าแชทและเครื่องมือ AI)
//  run({model, system, messages, onToken, kind}) -> {ok, text, charge} | {ok:false, code, message}
import { useCallback, useEffect, useState } from "react";
import * as engine from "../../services/ai/engine";
import { users } from "../../services/users";
import { useAuth } from "../../context/AuthContext";
import { isUsable } from "../../services/ai/models";

export function useEngineState() {
  const [s, setS] = useState(engine.getState());
  useEffect(() => engine.subscribe(setS), []);
  return s;
}

export function useAi(model) {
  const { profile, setAccount } = useAuth();
  const state = useEngineState();
  const [cached, setCached] = useState(null);
  const webllmId = model?.webllmId;
  const ready = !!webllmId && engine.isReadyFor(webllmId);

  // เปิดหน้า/เปลี่ยนโมเดล: โหลดให้อัตโนมัติทันที (อยู่ในแคช = เปิดจากแคช, ยังไม่มี = ดาวน์โหลดเลย ไม่ต้องกดปุ่ม)
  useEffect(() => {
    let alive = true;
    if (!webllmId || !isUsable(model)) return undefined;
    setCached(null);
    engine.isCached(webllmId).then((c) => alive && setCached(c));
    return () => {
      alive = false;
    };
  }, [webllmId]); // eslint-disable-line react-hooks/exhaustive-deps

  // เริ่มโหลดเมื่อเครื่องว่าง (รวมกรณีเปลี่ยนโมเดลระหว่างที่อีกตัวกำลังโหลด) — ล้มเหลวแล้วไม่วนโหลดซ้ำ รอผู้ใช้กด "ลองใหม่"
  const usable = isUsable(model);
  useEffect(() => {
    if (cached === null || !webllmId || !usable || !engine.isSupported()) return;
    if (state.status === "loading" || engine.isReadyFor(webllmId)) return;
    if (state.status === "error" && state.modelId === webllmId) return;
    engine.load(webllmId);
  }, [cached, webllmId, usable, state.status, state.modelId]);

  const download = useCallback(() => webllmId && engine.load(webllmId), [webllmId]);

  const run = useCallback(
    async ({ system, messages, onToken, kind = "chat", maxTokens, temperature, ref }) => {
      if (!profile?.uid) return { ok: false, code: "login", message: "กรุณาเข้าสู่ระบบก่อนใช้งาน" };
      if (!isUsable(model)) return { ok: false, code: "locked", message: `${model?.name || "โมเดลนี้"} ยังไม่เปิดให้บริการ` };
      const can = await users.canSpend(profile.uid);
      if (!can.ok) return { ok: false, code: can.code || "denied", message: can.message };
      if (!engine.isReadyFor(model.webllmId)) {
        const l = await engine.load(model.webllmId);
        if (!l.ok) return { ok: false, code: "ai_not_ready", message: l.message };
      }
      const r = await engine.generate({ system, messages, onToken, maxTokens, temperature });
      if (!r.ok) return r;
      let charge = null;
      try {
        charge = await users.charge(profile.uid, { usage: r.usage, model, kind, ref });
        const u = await users.get(profile.uid);
        if (u) setAccount(u);
      } catch (e) {
        /* หัก token ไม่สำเร็จ (เน็ตหลุด) — ไม่ทิ้งคำตอบที่ได้แล้ว */
      }
      return { ...r, charge };
    },
    [profile?.uid, model, setAccount]
  );

  return { state, ready, cached, download, run, abort: engine.abort, supported: engine.isSupported(), isMock: engine.isMockEngine() };
}
